import { fbm2 } from '@vm/shared';
import { M } from '../palette';
import { blocks, MapBuilder, planks, smoothField, symPoint, symYaw, yawToward, type Brush, type Side } from './builder';
import { flattenPad } from './kit';
import { emptyMeta, type CoreRegion, type MapDef, type MapMeta } from './types';

const S = 640;
const G = 32;
const SYM = 'mirrorX' as const;
/** West fort (team 0) outer walls; the east fort is its mirror. */
const FX0 = 40;
const FX1 = 200;
const FZ0 = 210;
const FZ1 = 430;
const WT = 10;
const WH = 40;
/** Core crystal inside the west bunker. */
const CORE: CoreRegion = { team: 0, x0: 127, y0: G + 4, z0: 312, x1: 143, y1: G + 20, z1: 328 };
const OASIS_R = 64;
const ISLAND_R = 24;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const rectW = (x: number, z: number, x0: number, z0: number, x1: number, z1: number, pad: number): number =>
  clamp01(1 - Math.max(x0 - x, x - x1, z0 - z, z - z1) / pad);

function terrain(b: MapBuilder): void {
  const h = new Int16Array(S * S);
  const surface = new Uint8Array(S * S);
  const dunes = smoothField(S, S, 4, (x, z) => fbm2(x * 0.011 + z * 0.004, z * 0.016, 401, 3));
  const tint = smoothField(S, S, 4, (x, z) => fbm2(x * 0.03, z * 0.03, 402, 2));
  const sand = [M('sand_1'), M('sand_3'), M('sand_2')];
  for (let z = 0; z < S; z++)
    for (let x = 0; x < S; x++) {
      const mx = x < S / 2 ? x : S - 1 - x;
      const flat = Math.max(
        rectW(mx, z, FX0 - 30, FZ0 - 30, FX1 + 50, FZ1 + 30, 30),
        rectW(mx, z, 190, 40, 330, 200, 24),
        rectW(mx, z, 220, 440, 330, 610, 24),
        clamp01((OASIS_R + 40 - Math.hypot(mx - 319.5, z - 319.5)) / 24),
      );
      const d = dunes(mx, z);
      // Dune crests: sharpened ridges of the noise.
      const ridge = Math.max(0, 1 - Math.abs(d - 0.5) * 4);
      const height = (ridge * 18 + Math.max(0, d - 0.55) * 24) * (1 - flat);
      h[x + z * S] = G + Math.floor(height / 4) * 4;
      surface[x + z * S] = sand[Math.min(2, Math.floor(tint(mx, z) * 3.4))];
    }
  b.heightfield({ height: h, surface, sub: [M('sand_2'), M('sand_1')], subDepth: 6, deep: M('rock_sand') });
}

/** Corner turret footprint, voxels. */
const TURRET = 58;

/**
 * Square corner turret: solid below the wall-walk, a 2.2 m room at walk level and a crenellated
 * roof. A 1.2 m flight runs along the room's outer wall (north or south), climbing toward the outer
 * side (west or east) from a 0.8 m landing, under an opening in the roof, and arrives flush with the
 * roof 0.6 m inside the battlements.
 */
function turret(b: MapBuilder, x0: number, z0: number, top: number, stone: Brush, trim: Brush, north: boolean, west: boolean): void {
  const walk = G + WH;
  const rise = top - walk;
  const size = TURRET;
  b.box(x0, G - 2, z0, x0 + size, top, z0 + size, stone);
  b.clear(x0 + 4, walk, z0 + 4, x0 + size - 4, top - 2, z0 + size - 4);
  // Room is x0 + 4 .. x0 + size - 4 (50 voxels square after the walls). A straight 34 degree flight of this 2.4 m climb needs 20 foot + 36 run + 10 landing = 66 voxels,
  // so the stairs are a U-turn along the outer wall: lane A (1.0 m) from a 2.0 m foot to a landing at the far end, lane B beside it back to the roof.
  const lane = 10;
  const dirA: Side = west ? 'w' : 'e';
  const startX = west ? x0 + size - 4 - 20 - 1 : x0 + 4 + 20;
  const az = north ? z0 + 4 : z0 + size - 4 - 2 * lane;
  const [fa, fb, land] = b.switchbackFlight(startX, walk, az, dirA, lane, rise, trim, 'a', 3);
  // Roof opening over both lanes and the landing (head room for everything you stand on).
  b.openAbove(top - 3, top, [fa, fb, land], 4);
  b.crenellate(x0, z0, x0 + size, z0 + size, top, 7, 3, trim, 5, 4);
}

function westFort(b: MapBuilder): void {
  const ss: Brush = blocks([M('sandstone_1')], 3, 0);
  const ss2: Brush = blocks([M('sandstone_2')], 3, 1);
  const ss3: Brush = blocks([M('sandstone_3')], 3, 2);
  const walk = G + WH;
  // Courtyard floor and outer walls with a wall-walk and square crenellations.
  b.box(FX0, G - 1, FZ0, FX1, G, FZ1, blocks([M('sandstone_3')], 3, 3));
  b.clear(FX0, G, FZ0, FX1, G + 20, FZ1);
  b.walls(FX0, G - 2, FZ0, FX1, walk, FZ1, WT, ss);
  b.crenellate(FX0, FZ0, FX1, FZ1, walk, 7, 3, ss3, 5, 4);
  // East gate with a timber lintel and open doors.
  b.clear(FX1 - WT, G, 300, FX1, G + 30, 340);
  b.box(FX1 - WT - 1, G + 30, 296, FX1 + 1, G + 34, 344, M('log_2'));
  // The door leaves stand open against the gate jambs, 0.6 m deep: any deeper and they pinch the corridor beside the bunker to a
  // 0.8 m slot that bots cannot path through (their grid needs 0.7 m boxes on a 0.5 m lattice), which sealed the whole fort.
  b.box(FX1 - WT - 6, G, 300, FX1 - WT, G + 28, 302, planks([M('planks_dark_1')], 0));
  b.box(FX1 - WT - 6, G, 338, FX1 - WT, G + 28, 340, planks([M('planks_dark_1')], 0));
  b.box(FX1 - 2, G + 32, 308, FX1, G + 48, 332, M('team_red'));
  // Corner turrets, centered on the wall corners.
  const tOff = (TURRET - WT) >> 1;
  for (const [x, z] of [[FX0 - tOff, FZ0 - tOff], [FX1 - WT - tOff, FZ0 - tOff], [FX0 - tOff, FZ1 - WT - tOff], [FX1 - WT - tOff, FZ1 - WT - tOff]]) {
    const north = z < 300;
    const west = x < 100;
    turret(b, x, z, G + 64, ss2, ss3, north, west);
    // Doors from both wall-walks into the turret room (each opens beside the stair landing or the
    // side of the flight).
    const wx = west ? FX0 + WT / 2 : FX1 - WT / 2;
    const wz = north ? FZ0 + WT / 2 : FZ1 - WT / 2;
    b.clear(wx - 5, walk, north ? z + TURRET - 6 : z, wx + 5, walk + 20, north ? z + TURRET : z + 6);
    b.clear(west ? x + TURRET - 6 : x, walk, wz - 5, west ? x + TURRET : x + 6, walk + 20, wz + 5);
  }
  // Stairs from the courtyard up to the wall-walk along the north and south walls.
  b.climb(FX0 + 60, G, FZ0 + WT, 'e', 12, WH, ss2);
  b.climb(FX0 + 60, G, FZ1 - WT - 12, 'e', 12, WH, ss2);

  // The bunker: thick sandstone around the core, a door facing the spawn yard and a barred
  // window toward the gate, with stairs to its roof.
  const bx0 = 100;
  const bx1 = 170;
  const bz0 = 280;
  const bz1 = 360;
  const bTop = G + 34;
  b.box(bx0, G - 1, bz0, bx1, bTop, bz1, ss2);
  b.clear(bx0 + 16, G, bz0 + 21, bx1 - 16, G + 28, bz1 - 21);
  b.box(bx0 + 16, G - 1, bz0 + 21, bx1 - 16, G, bz1 - 21, M('tile_white'));
  b.clear(bx0, G, 314, bx0 + 16, G + 24, 326);
  b.clear(bx1 - 16, G + 8, 316, bx1, G + 22, 324);
  for (let z = 316; z < 324; z += 3) b.box(bx1 - 8, G + 8, z, bx1 - 6, G + 22, z + 1, M('steel_dark_1'));
  b.box(CORE.x0 - 3, G, CORE.z0 - 3, CORE.x1 + 3, CORE.y0, CORE.z1 + 3, M('gold'));
  b.box(CORE.x0, CORE.y0, CORE.z0, CORE.x1, CORE.y1, CORE.z1, M('core_red'));
  for (const [x, z] of [[bx0 + 17, bz0 + 22], [bx1 - 18, bz0 + 22], [bx0 + 17, bz1 - 23], [bx1 - 18, bz1 - 23]]) b.box(x, G, z, x + 1, G + 28, z + 1, M('team_red_glow'));
  b.walls(bx0, bTop, bz0, bx1, bTop + 5, bz1, 2, ss3);
  b.climbEnding(bx0 + 20, G, bz1, 'e', 10, bTop - G, ss3);
  b.clear(bx0 + 20 + (bTop - G) - 4, bTop, bz1 - 2, bx0 + 20 + (bTop - G) + 2, bTop + 5, bz1);
  // Awnings and sandbags around the yard (the awning keeps clear of the wall-walk stairs).
  b.box(74, G + 24, 234, 100, G + 25, 262, M('cloth_white'));
  for (const z of [234, 258]) b.box(98, G, z, 100, G + 24, z + 2, M('log_1'));
  b.sandbags(FX1 + 20, 286, FX1 + 28, 300, G, 9);
  b.sandbags(FX1 + 20, 340, FX1 + 28, 354, G, 9);
  b.crate(FX0 + 90, G, FZ0 + 20);
  b.crate(FX0 + 100, G, FZ0 + 22, 8);
  b.barrel(FX0 + 110, G, FZ1 - 30, M('team_red'));
  b.barrel(FX0 + 120, G, FZ1 - 26, M('paint_green'));
}

/** Clay pot. */
function pot(b: MapBuilder, x: number, z: number, h = 7): void {
  b.cylinder(x, z, G, G + h, 2.6, M('clay'));
  b.cylinder(x, z, G + h, G + h + 1, 1.6, M('clay'));
}

/** Market (north), oasis (center) and ruins (south), built in the west half and mirrored. */
function middle(b: MapBuilder): void {
  const ss: Brush = blocks([M('sandstone_1')], 3, 4);
  const ss3: Brush = blocks([M('sandstone_3')], 3, 5);
  // Market square: paving, stalls, tents, carpets, pots and a well.
  b.box(200, G - 1, 50, 320, G, 196, blocks([M('sandstone_3')], 3, 6));
  b.stall(214, G, 70, M('cloth_red'));
  b.stall(250, G, 70, M('cloth_yellow'));
  b.stall(214, G, 160, M('cloth_green'));
  b.tent(252, G, 130, true, 40, 30, M('cloth_blue'));
  b.box(284, G, 70, 304, G + 1, 100, M('carpet_red'));
  b.box(288, G + 1, 74, 300, G + 2, 96, M('paint_yellow'));
  for (const [x, z] of [[292, 110], [298, 114], [292, 118], [276, 180], [282, 186]]) pot(b, x, z);
  b.crate(240, G, 100);
  b.crate(240, G + 10, 102, 8);
  b.tntBarrel(270, G, 104);
  for (let z = 80; z < 160; z++)
    for (let x = 300; x < 320; x++) {
      const d = Math.hypot(x - 319.5, z - 119.5);
      if (d <= 16 && d > 12) b.box(x, G, z, x + 1, G + 8, z + 1, ss3);
      else if (d <= 12) {
        b.clear(x, G - 12, z, x + 1, G, z + 1);
        b.box(x, G - 13, z, x + 1, G - 5, z + 1, M('water'));
      }
    }
  for (const x of [304]) b.box(x, G + 8, 118, x + 2, G + 30, 122, M('log_1'));
  b.box(304, G + 30, 106, 320, G + 32, 134, M('planks_4'));
  // Arcade of arches along the market's west side with a cloth roof.
  for (let z = 50; z < 196; z += 24) b.box(196, G, z, 204, G + 34, z + 8, ss);
  b.box(196, G + 34, 50, 204, G + 38, 196, ss);
  b.box(204, G + 30, 50, 216, G + 31, 196, M('cloth_yellow'));

  // Oasis: a ring of water around a palm island, grass banks and reeds.
  for (let z = 320 - OASIS_R - 16; z < 320 + OASIS_R + 16; z++)
    for (let x = 320 - OASIS_R - 16; x < 320; x++) {
      const d = Math.hypot(x - 319.5, z - 319.5);
      if (d > OASIS_R + 14) continue;
      b.clear(x, G - 1, z, x + 1, G + 12, z + 1);
      if (d <= ISLAND_R) b.box(x, G - 1, z, x + 1, G, z + 1, d > ISLAND_R - 3 ? M('sand_3') : M('grass_3'));
      else if (d <= OASIS_R) {
        const depth = Math.min(d - ISLAND_R, OASIS_R - d) < 6 ? 2 : 5;
        b.clear(x, G - 1 - depth, z, x + 1, G, z + 1);
        b.box(x, G - 2 - depth, z, x + 1, G - 1 - depth, z + 1, M('sand_2'));
        b.box(x, G - 1 - depth, z, x + 1, G - 1, z + 1, M('water'));
      } else b.box(x, G - 1, z, x + 1, G, z + 1, d < OASIS_R + 6 ? M('grass_1') : M('grass_3'));
    }
  for (const [x, z, h, dir] of [[252, 280, 50, 'w'], [262, 372, 56, 's'], [300, 250, 46, 'n'], [244, 330, 44, 'w'], [306, 322, 40, 'e']] as const) b.palm(x, G, z, h, dir);
  for (const [x, z] of [[258, 300], [268, 350], [290, 262], [282, 380]]) {
    for (let k = 0; k < 4; k++) b.box(x + (k % 2) * 3, G - 1, z + (k >> 1) * 3, x + (k % 2) * 3 + 1, G + 8 + k * 2, z + (k >> 1) * 3 + 1, M('bamboo'));
  }

  // South ruins: a watch post raised on four pillars, broken walls and rubble.
  const wx0 = 294;
  const wz0 = 500;
  // The lookout room is 2.4 m tall with shooting slits at eye height and a 2.3 m door over the top
  // of the stairs (the auto-step needs head room above the last step).
  for (const [dx, dz] of [[0, 0], [0, 40], [40, 0], [40, 40]]) b.box(wx0 + dx - 4, G, wz0 + dz - 4, wx0 + dx + 4, G + 44, wz0 + dz + 4, ss);
  b.box(wx0 - 6, G + 44, wz0 - 6, wx0 + 46, G + 47, wz0 + 46, planks([M('planks_2')], 0));
  b.walls(wx0 - 6, G + 47, wz0 - 6, wx0 + 46, G + 71, wz0 + 46, 3, ss3);
  b.clear(wx0 + 10, G + 56, wz0 - 6, wx0 + 16, G + 64, wz0 + 46);
  b.clear(wx0 - 6, G + 56, wz0 + 10, wx0 + 46, G + 64, wz0 + 16);
  b.clear(wx0 + 11, G + 47, wz0 + 43, wx0 + 25, G + 70, wz0 + 46);
  flattenPad(b, wx0 + 6, wz0 + 88, wx0 + 30, wz0 + 152, G, M('sand_1')); // the dune that stood here put the first step on a slope
  b.climbEnding(wx0 + 12, G, wz0 + 90, 'n', 12, 47, planks([M('planks_1')], 0), { thick: 3 });
  b.box(wx0 - 8, G + 71, wz0 - 8, wx0 + 48, G + 73, wz0 + 48, M('cloth_white'));
  for (const [x, z, w, hh] of [[230, 470, 40, 22], [236, 580, 30, 14], [262, 548, 6, 30]] as const) b.box(x, G, z, x + w, G + hh, z + 6, ss);
  b.cone(250, G, 500, 10, 6, M('sandstone_2'));
  b.tntCrate(236, G, 520, 7);
  for (const [x, z, h, dir] of [[220, 610, 48, 'e'], [120, 480, 52, 'n'], [110, 150, 46, 's'], [40, 600, 50, 'e'], [40, 40, 44, 's']] as const) b.palm(x, b.surfaceY(x, z, 90), z, h, dir);
}

function build(b: MapBuilder): MapMeta {
  terrain(b);
  westFort(b);
  middle(b);
  b.symmetrize(SYM);

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  const team0: [number, number][] = [
    [5.8, 26.5], [8.2, 26.5], [5.8, 29], [8.2, 29], [5.8, 35], [8.2, 35], [5.8, 37.5], [8.2, 37.5],
  ];
  for (const [x, z] of team0) {
    const yaw = -Math.PI / 2;
    meta.spawns.push({ team: 0, x, y: gy, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  const ffa: [number, number][] = [
    [3, 5], [61, 5], [3, 59], [61, 59], [16, 17], [48, 17], [16, 47], [48, 47], [21, 5], [43, 5], [21, 59], [43, 59],
    [24, 24], [40, 24], [24, 42], [40, 42], [12, 58], [52, 58],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x, y: b.standAt(x, z, 6.5), z, yaw: yawToward(x, z, 32, 32) });
  meta.cores.push({ ...CORE });
  meta.cores.push({ team: 1, x0: S - CORE.x1, y0: CORE.y0, z0: CORE.z0, x1: S - CORE.x0, y1: CORE.y1, z1: CORE.z1 });
  meta.points.push({ id: 'A', name: 'Bazaar', x: 32, y: gy, z: 16, r: 3.5 });
  meta.points.push({ id: 'B', name: 'Oasis', x: 32, y: gy, z: 32, r: 3 });
  meta.points.push({ id: 'C', name: 'Watch Post', x: 32, y: gy, z: 52, r: 3.5 });
  meta.flags.push({ team: 0, x: 7, y: gy, z: 32 }, { team: 1, x: 57, y: gy, z: 32 });
  for (const [x, z, kind] of [[22.5, 32, 'health'], [23, 12, 'armor'], [27.5, 45, 'health']] as const) {
    meta.pickups.push({ kind, x, y: b.standAt(x, z, 6.5), z });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.pickups.push({ kind, x: mx, y: b.standAt(mx, mz, 6.5), z: mz });
  }
  return meta;
}

export const duneFort: MapDef = {
  id: 'dune_fort',
  name: 'Dune Fort',
  description: 'Twin sandstone forts guard their crystals across the dunes. Hold the oasis, the bazaar and the old watch post.',
  size: [S, 192, S],
  modes: ['demolition', 'tdm', 'dom', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 5150,
  version: 2,
  theme: {
    skyTop: 0x4a8ee0,
    skyHorizon: 0xf2dcb4,
    fog: 0xecd8b4,
    fogNear: 45,
    fogFar: 150,
    sun: [0.25, 0.85, -0.4],
    sunColor: 0xfff0d0,
    sunIntensity: 1.05,
    ambient: 0xc8d4ec,
    ambientIntensity: 0.52,
    outside: 0xd8bc80,
    music: 'desert',
  },
  build,
};
